import { MenuItem } from "@progress/kendo-react-layout";
import { GridColumn } from "@progress/kendo-react-grid";
import { routes } from "../../app/routes";
import { UserModel, UserOrganizationModel } from "../../data/entities";
import { UserOrganizationService, UserService } from "../../services";
import { useGridActions, useCRUDHandlers, useGridSearch, useSetPageTitle, useSharedState, useAsync, usePermission } from "../../hooks";
import { Breakpoints, GridSearchBox, ODataGrid, ResponsiveGridColumn, TitlelessCard } from "../../components";
import { appendODataFilters, createODataQueryFilter, createODataSearchFilters } from "../../utils";
import { getUserInfo, isSysAdmin, isTenantAdmin } from "../../app";
import { useSelector } from "react-redux";
import { usePageActions } from "../PageLayout";

export function UserIndexPage() {

  useSetPageTitle("Users", []);

  const gridData = useSharedState<Array<UserOrganizationModel>>([]);
  const userGridData = useSharedState<Array<UserModel>>([]);

  const userOrganizationService = new UserOrganizationService();
  const userService = new UserService(); 

  const { onClick, onSelect } = useGridActions<UserOrganizationModel>(routes.user.details);
  const { onClick: userOnClick, onSelect: userOnSelect } = useGridActions<UserModel>(routes.user.details);
  const { gridRef, getItems } = useCRUDHandlers<UserOrganizationModel>(userOrganizationService, "UserOrganization");
  const {gridRef: userGridRef, getItems: userGetItems} = useCRUDHandlers<UserModel>(userService, "User");
 
  const tenantAdmin = useSelector(isTenantAdmin());
  const systemAdmin = useSelector(isSysAdmin());

  const isAdminView = tenantAdmin || systemAdmin;
  const { searchText, setSearchText } = useGridSearch(isAdminView ? userGridRef : gridRef);
  const searchFields = isAdminView
    ? ["userName", "firstName", "lastName"]
    : ["user/userName", "user/firstName", "user/lastName"];

  usePageActions(<GridSearchBox onSearch={setSearchText} />, []);

  const userId = useSelector(getUserInfo())?.id ?? -1;
  const {value: orgIds } = useAsync(async () => await userOrganizationService.getByUserIdAsync(userId), []);

  const gridKey = orgIds?.join(",") ?? "no-orgs";
  
  const onRowClickHandler = (e: any) => {
    const userInfo = e.dataItem;  
    const userId = userInfo.user?.id;
 
    (tenantAdmin || systemAdmin) ? userOnClick(e, new Map([["id", `${userInfo.id}`]])) : onClick(e, new Map([["id", `${userId}`]]));  
  };
  
  if (!orgIds) return null;

  return (
    <TitlelessCard className="p-0 m-0" bodyClassName="p-0">
      <ODataGrid
        key={gridKey}
        ref={(tenantAdmin||systemAdmin)? userGridRef : gridRef}
        getData={async (oDataQuery) => {
          
          let queryFilter;
          if(tenantAdmin ||systemAdmin)
          {
            return userGetItems(appendODataFilters(oDataQuery, createODataSearchFilters(searchText, searchFields)));
          }else{
            const orgFilter = orgIds && orgIds.length > 0
            ? [`(${orgIds.map(id => `organizationId eq ${id}`).join(' or ')})`] : [];
             queryFilter = createODataQueryFilter(oDataQuery, [...orgFilter, ...createODataSearchFilters(searchText, searchFields)])
          
          try {
            const data = await getItems(queryFilter + `&$expand=User`); 
            const deduplicated = [];
            const seenUserIds = new Set();
        
            for (const item of data.value) {
              if (!seenUserIds.has(item.userId)) {
                seenUserIds.add(item.userId);
                deduplicated.push(item);
              }
            }
      
            return {
              "@odata.context": data["@odata.context"], 
              "@odata.count": deduplicated.length,       
              "value": deduplicated                      
            };  
          } catch (error) {
            return {
              "@odata.context": "",   
              "@odata.count": 0,      
              "value": []            
            };
          }
        }
        }}
        dataState={(tenantAdmin||systemAdmin) ? userGridData : gridData}
        onRowMenuSelect={onSelect}
        onRowClick={onRowClickHandler}
        sort={[{ field:(tenantAdmin||systemAdmin) ? "firstName" :"user.firstName", dir: "asc" }]}
        scrollable="scrollable"
        filterable={false}
      >
        <ResponsiveGridColumn
          title="Username"
          field={(tenantAdmin||systemAdmin) ? "userName" :"user.userName"}
          sortable={true}
          filter="text"
        />

        <ResponsiveGridColumn
          title="First Name"
          field={(tenantAdmin||systemAdmin) ? "firstName" :"user.firstName"}
          sortable={true}
          filter="text"
          breakpoint={Breakpoints.md}
        />
        <ResponsiveGridColumn
          title="Last Name"
          field={(tenantAdmin||systemAdmin) ? "lastName" :"user.lastName"}
          sortable={true}
          filter="text"
          breakpoint={Breakpoints.lg}
        />
        <MenuItem
          text="Open"
          data={{ action: "open" }}
          icon="hyperlink-open"
        />
        <MenuItem
          text="Open Tab"
          data={{ action: "opentab" }}
          icon="windows"
        />
      </ODataGrid>
    </TitlelessCard>
  );
}

export default UserIndexPage;

